<template>
  <KekkleBottomSheet
    v-model:open="isOpen"
    shadow="blue"
    padding="normal"
    initial-height="full"
  >
    <div class="dark:text-slate-200">
      <!-- Header -->
      <div class="mb-6 flex items-center justify-between">
        <h2 class="flex-1 text-2xl font-bold">
          {{ $t("pages.event.add_event") }}
        </h2>
        <DefaultButton
          color="blue"
          :disabled="!validated"
          :loading="status.submitting"
          @click="submit"
        >
          {{ $t("global.inputs.labels.save") }}
        </DefaultButton>
      </div>

      <!-- Form -->
      <div class="mb-4 w-full">
        <text-input
          v-model="name"
          :placeholder="$t('global.inputs.placeholders.event')"
          :label="$t('global.inputs.labels.name_event')"
          :has-error="validationErrors.name"
          :error-message="$t('global.inputs.errors.name_event')"
        />
      </div>

      <div class="mb-4 w-full">
        <text-area-input
          v-model="description"
          :has-error="validationErrors.description"
          :error-message="$t('global.inputs.errors.description_event')"
          :label="$t('global.inputs.labels.description_event')"
          :placeholder="$t('global.inputs.placeholders.event_description')"
        />
      </div>

      <div class="mb-4 w-full">
        <p class="font-medium dark:text-slate-200">
          {{ $t("global.inputs.labels.date_time") }}*
        </p>
        <p
          v-show="validationErrors.timestamp"
          class="mt-1 text-sm text-red-600"
        >
          {{ $t("global.inputs.errors.date_time") }}
        </p>

        <div class="flex w-full flex-col items-center">
          <date-picker
            v-model.range="range"
            range
            is24hr
            mode="dateTime"
            class="mx-auto border-2 border-solid border-black-dark shadow-blue-hard-sm dark:bg-kekkle-dark-light"
            show-iso-weeknumbers
            expanded
            :attributes="attributes"
            :is-dark="darkModeEnabled"
          />
        </div>
      </div>
    </div>
  </KekkleBottomSheet>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { DatePicker } from "v-calendar";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useEventsStore } from "@/stores/modules/events/events";
import { useDarkModeStore } from "@/stores/modules/darkmode/darkmode";
import {
  setSuccessToast,
  setWarningToast,
} from "@/helpers/notifications/toast";
import TextInput from "@/components/Inputs/TextInput.vue";
import TextAreaInput from "@/components/Inputs/TextAreaInput.vue";
import KekkleBottomSheet from "@/components/ui/KekkleBottomSheet.vue";
import DefaultButton from "@/components/Buttons/DefaultButton.vue";

const { t } = useI18n();
const authStore = useAuthStore();
const eventsStore = useEventsStore();
const darkModeStore = useDarkModeStore();

const isOpen = defineModel<boolean>("open", { required: true });

// Form state
const status = ref({
  submitting: false,
});
const name = ref<string>("");
const description = ref<string>("");
const range = ref<any>({});

// Computed properties
const darkModeEnabled = computed(() => darkModeStore.darkModeEnabled);

const attributes = computed(() => [
  {
    key: "today",
    highlight: {
      fillMode: "light",
      color: "orange",
    },
    dates: new Date(),
  },
]);

const nameValid = computed(() => !!(name.value && name.value.length > 0));

const descriptionValid = computed(
  () => !!(description.value && description.value.length > 0),
);

const timestampValid = computed(
  () =>
    !!(
      range.value.start &&
      range.value.end &&
      range.value.start.getTime() < range.value.end.getTime()
    ),
);

const validationErrors = computed(() => ({
  name: !nameValid.value,
  description: !descriptionValid.value,
  timestamp: !timestampValid.value,
}));

const validated = computed(() => {
  return !Object.values(validationErrors.value).some((value) => value === true);
});

// Methods
function resetForm() {
  name.value = "";
  description.value = "";
  const now = new Date();
  const startTime = new Date(now);
  startTime.setHours(12, 0, 0, 0);
  const endTime = new Date(now);
  endTime.setHours(13, 0, 0, 0);
  range.value = {
    start: startTime,
    end: endTime,
  };
}

async function submit() {
  if (!validated.value) {
    setWarningToast({
      message: t("global.toast.check_all_fields"),
    });
    return;
  }

  try {
    status.value.submitting = true;
    await eventsStore.addEvent({
      name: name.value!,
      description: description.value!,
      begin_timestamp: range.value.start!,
      end_timestamp: range.value.end!,
      created_by: authStore.authUuid!,
      available: [],
      non_available: [],
      maybe_available: [],
    });

    setSuccessToast({
      message: t("global.toast.event_saved"),
      emoji: "📅",
    });
    isOpen.value = false;
  } catch (e) {
    console.error("Error adding event:", e);
  } finally {
    status.value.submitting = false;
  }
}

// Watch for opening to reset form
watch(isOpen, (newValue) => {
  if (newValue) {
    resetForm();
  }
});
</script>
